import { useCallback, useEffect, useRef } from 'react';
import { useAppDispatch } from './store';
import { setHoldingsResult, setHoldingsError } from '@src/app/store/reducers/holdings';
import { GetHoldingsRequest } from '@src/app/store/reducers/holdings/types';
import { MultiPortfolioHoldingList } from 'clientportalshared';
import { initiateHoldingsAsync, getHoldingsProgressAsync } from '@src/app/store/reducers/holdings/thunks';

const POLLING_INTERVAL_MS = 1000;

const filterBySecurityCode = (result: MultiPortfolioHoldingList, securityCode?: string): MultiPortfolioHoldingList => {
  if (!securityCode) return result;
  return {
    totalValue: result.totalValue,
    holdingList: {
      asOfDate: result.holdingList?.asOfDate,
      data: result.holdingList?.data?.filter((h) => h.securityCode === securityCode) ?? [],
    },
  };
};

export const useHoldingsPolling = (): { fetchHoldings: (request: GetHoldingsRequest) => Promise<void> } => {
  const dispatch = useAppDispatch();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const fetchHoldings = useCallback(
    async (request: GetHoldingsRequest) => {
      if (!request.asOfDate) return;

      stopPolling();
      cancelledRef.current = false;

      try {
        const progressId = await dispatch(initiateHoldingsAsync(request)).unwrap();
        if (cancelledRef.current) return;

        const poll = async () => {
          if (cancelledRef.current) return;
          try {
            const { status, result } = await dispatch(getHoldingsProgressAsync(progressId)).unwrap();
            if (cancelledRef.current) return;

            const normalisedStatus = (status ?? '').toLowerCase();

            if (normalisedStatus === 'completed' || result) {
              stopPolling();
              if (result) dispatch(setHoldingsResult(filterBySecurityCode(result, request.securityCode)));
            } else if (normalisedStatus === 'error') {
              stopPolling();
              dispatch(setHoldingsError());
            } else {
              timerRef.current = setTimeout(poll, POLLING_INTERVAL_MS);
            }
          } catch {
            if (!cancelledRef.current) {
              stopPolling();
              dispatch(setHoldingsError());
            }
          }
        };

        timerRef.current = setTimeout(poll, POLLING_INTERVAL_MS);
      } catch {
        if (!cancelledRef.current) dispatch(setHoldingsError());
      }
    },
    [dispatch, stopPolling]
  );

  useEffect(
    () => () => {
      cancelledRef.current = true;
      stopPolling();
    },
    [stopPolling]
  );

  return { fetchHoldings };
};
